<template>
  <div class="w-full overflow-hidden rounded-lg">
    <button
      @click="toggle"
      :data-testid="testId"
      class="flex w-full items-center justify-between p-4 text-left"
      :class="{
        'bg-[#F7DAE2] text-neutral50': props.approvalStatus === 'approved',
        'bg-white text-neutral50': props.approvalStatus !== 'approved'
      }"
    >
      <div class="flex items-center gap-2">
        <svg
          v-if="props.icon"
          data-testid="dropdown-card-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="h-5 w-5 shrink-0"
          :class="{
            'text-neutralLight':
              props.approvalStatus === null || props.approvalStatus === undefined,
            'text-negative': props.approvalStatus === 'approved',
            'text-neutral50': props.approvalStatus === 'rejected'
          }"
        >
          <path fill-rule="evenodd" :d="props.icon" clip-rule="evenodd"></path>
        </svg>
        <div class="flex flex-col">
          <span class="font-bold">{{ title }}</span>
          <span v-if="props.subtitle" class="text-sm font-normal text-[#68688D]">
            {{ props.subtitle }}
          </span>
        </div>
      </div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        class="h-6 w-6 shrink-0 transform transition-transform duration-300"
        :class="{ 'rotate-180': !isOpen }"
      >
        <path :d="mdiChevronUp"></path>
      </svg>
    </button>

    <Transition name="expand" @enter="enter" @after-enter="afterEnter" @leave="leave">
      <div v-if="isOpen" data-testid="dropdown-card-body" class="overflow-hidden bg-white">
        <div class="flex flex-col gap-y-4 border-t border-lightGray30 p-4">
          <slot></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiChevronUp } from '@mdi/js'

type ApprovalStatusOption = 'approved' | 'rejected' | null

const props = withDefaults(
  defineProps<{
    title: string
    icon?: string
    approvalStatus?: ApprovalStatusOption
    subtitle?: string
    startOpen?: boolean
    testId?: string
  }>(),
  {
    icon: undefined,
    approvalStatus: undefined,
    subtitle: undefined,
    startOpen: false,
    testId: undefined
  }
)

// CONTRATO INVERTIDO (intencional, documentado em DropdownCard.stories.ts):
// startOpen=false → isOpen=true → inicia ABERTO. startOpen=true → inicia FECHADO.
const isOpen = ref(!props.startOpen)

const toggle = () => {
  isOpen.value = !isOpen.value
}

const enter = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = 'auto'
  const height = getComputedStyle(el).height
  el.style.height = '0'
  getComputedStyle(el)
  setTimeout(() => {
    el.style.height = height
  })
}

const afterEnter = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = 'auto'
}

const leave = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = getComputedStyle(el).height
  getComputedStyle(el)
  setTimeout(() => {
    el.style.height = '0'
  })
}
</script>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition: height 0.3s ease-in-out;
  overflow: hidden;
}
</style>
